import * as XLSX from 'xlsx'
import { serviceLabel, laneShipments } from '../apiMap'

// Client-side file generation for the D2C output pack. Every row here is built
// from REAL version data (the pricing engine's computedLanes) — no mock/dummy.
// The SL pack never includes cost/margin (the backend masks those for SL roles).

const svcLabel = (t) => serviceLabel(t)

/** Trigger a real .xlsx download in the browser from an array of row objects. */
export function downloadXlsx(filename, rows, sheetName = 'Sheet1') {
  const ws = XLSX.utils.json_to_sheet(rows && rows.length ? rows : [{ '': 'No data' }])
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName)
  XLSX.writeFile(wb, filename)
}

/** Build a real multi-sheet .xlsx from sheets: [{ name, rows: [{...}, ...] }] (row objects). */
export function downloadSheets(filename, sheets) {
  const wb = XLSX.utils.book_new()
  sheets.forEach((s) => {
    const ws = XLSX.utils.json_to_sheet(s.rows && s.rows.length ? s.rows : [{ '': 'No data' }])
    XLSX.utils.book_append_sheet(wb, ws, String(s.name).slice(0, 31))
  })
  XLSX.writeFile(wb, filename)
}

/** Build a real multi-sheet .xlsx from sheets: [{ name, aoa: [[cell, ...], ...] }]. */
export function downloadWorkbook(filename, sheets) {
  const wb = XLSX.utils.book_new()
  sheets.forEach((s) => {
    const ws = XLSX.utils.aoa_to_sheet(s.aoa)
    XLSX.utils.book_append_sheet(wb, ws, String(s.name).slice(0, 31))
  })
  XLSX.writeFile(wb, filename)
}

/** Read an uploaded .xlsx back into { sheetName: aoa } (array-of-arrays per sheet). */
export async function readWorkbook(file) {
  const buf = await file.arrayBuffer()
  const wb = XLSX.read(buf, { type: 'array' })
  const out = {}
  wb.SheetNames.forEach((n) => { out[n] = XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1, blankrows: false }) })
  return out
}

/** Build an .xlsx as an uploadable File (for multipart upload endpoints). */
export function xlsxFile(filename, rows, sheetName = 'Sheet1') {
  const ws = XLSX.utils.json_to_sheet(rows && rows.length ? rows : [{ '': '' }])
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, String(sheetName).slice(0, 31))
  const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' })
  return new File([buf], filename, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

/** Header-only template (.xlsx) — real column structure for the user to fill, no values. */
export function downloadTemplate(filename, headers, sheetName = 'Template') {
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.aoa_to_sheet([headers])
  XLSX.utils.book_append_sheet(wb, ws, String(sheetName).slice(0, 31))
  XLSX.writeFile(wb, filename)
}

/** Reviewer-only (L1/FH) margin working — includes cost/margin (masked for SL by the backend). */
export function marginWorkingRows(lanes = []) {
  return lanes.map((l) => ({
    Service: svcLabel(l.serviceType),
    Courier: l.courierParent || 'Blended',
    // D2C Courier Selection Pivot §4 — the resolved account per lane; courierId has been
    // on every lane object all along, just never mapped to a column before.
    'Account Code': l.courierId || '—',
    Zone: l.zone,
    'Weight slab': l.weightSlab,
    Payment: l.paymentMode,
    // allocatedShipments (FE Integration Guide v3 §2) — volumeShipments repeats the FULL
    // lane total on every priority row; summing it overcounts ~10x. This is the courier's
    // actual share (volumeShipments × allocationPct/100) — the number that should be summed.
    'Monthly shipments': l.allocatedShipments,
    'Revenue rate (Rs)': l.revenueRate,
    'Cost rate (Rs)': l.costRate,
    'Margin (Rs)': l.marginAbs,
    'Margin %': l.marginPct,
  }))
}

/** Reviewer-only allocation with CP account codes. */
export function allocationInternalRows(lanes = []) {
  return lanes.map((l) => ({
    Service: svcLabel(l.serviceType),
    'Courier (parent)': l.courierParent || 'Blended',
    'CP account code': l.courierId || '—',
    Zone: l.zone,
    'Weight slab': l.weightSlab,
    Priority: l.priority,
    'Allocation %': l.allocationPct,
    Payment: l.paymentMode,
  }))
}

/** Exception & risk report — only the guardrail-flagged lanes. */
export function exceptionRows(lanes = []) {
  return lanes.filter((l) => l.flagged).map((l) => ({
    Service: svcLabel(l.serviceType),
    Courier: l.courierParent || 'Blended',
    Zone: l.zone,
    'Weight slab': l.weightSlab,
    'Margin %': l.marginPct,
    Issue: 'Below guardrail (MAX 10% / Rs 3)',
  }))
}

/** Allocation — BD view: courier parent × service × zone × allocation (SL-safe, no CP codes). */
export function allocationBdRows(lanes = []) {
  return lanes.map((l) => ({
    Service: svcLabel(l.serviceType),
    'Courier (parent)': l.courierParent || 'Blended',
    Zone: l.zone,
    'Weight slab': l.weightSlab,
    Payment: l.paymentMode,
    Priority: l.priority,
    'Allocation %': l.allocationPct,
    // allocatedShipments, not volumeShipments — see marginWorkingRows for why.
    'Monthly shipments': l.allocatedShipments,
  }))
}

/** Serviceability & TAT: zone × weight slab × transit days per service (real minTat/maxTat). */
export function serviceabilityRows(lanes = []) {
  // De-dupe to one row per (service, zone, weight slab) — TAT/serviceability is courier-independent.
  const seen = new Map()
  lanes.forEach((l) => {
    const k = `${l.serviceType}|${l.zone}|${l.weightSlab}`
    if (!seen.has(k)) {
      seen.set(k, {
        Service: svcLabel(l.serviceType),
        Zone: l.zone,
        'Weight slab': l.weightSlab,
        Serviceable: 'Yes',
        // null (not 0) means the backend has no TAT data for this lane — show that
        // explicitly, don't let a blank cell read as "0-day / same-day delivery".
        'Min TAT (days)': l.minTat != null ? l.minTat : 'N/A',
        'Max TAT (days)': l.maxTat != null ? l.maxTat : 'N/A',
        'Monthly shipments': l.volumeShipments,
      })
    }
  })
  return [...seen.values()]
}

// ── Rate Card Margin Working — courier × weight-slab pivot, per-zone column groups ──
// A DIFFERENT layout from Internal Margin Working (one row per lane): here each row is
// one (service, courier, weight slab) and every zone becomes a group of margin/rate
// columns. Product shared this as the production rate-card workbook. Every figure is the
// volume-weighted blend of the matching computedLanes across payment mode + priority —
// the SAME Σ(x·vol)/Σvol the on-screen heatmap uses, so a cell here equals the heatmap cell.
//
// Derived from `lanes` alone: volumeShipments is the (zone×slab) shipment total repeated
// on every courier/priority row, so de-duping by (service, slab, zone) recovers the
// per-service totals the Volume / Weight-flag % / Overall % columns need — no need to
// thread the version's services[]/rfiEntries through the card.
// NOTE (FE-changes doc §4): the two "split %" columns are documented pending-product-
// confirmation defaults — weight-flag % = the slab's share of service shipments;
// overall % = (courier+slab allocated shipments) ÷ service shipments.
const RC_ZONES = ['Local', 'Within State', 'Metros', 'ROI', 'Special']
const rc1 = (n) => (n == null ? null : Math.round(n * 10) / 10)
const rc2 = (n) => (n == null ? null : Math.round(n * 100) / 100)
// shipment-weighted average of sel() over lanes — weight = each lane's ACTUAL allocated
// shipments (laneShipments), NOT volumeShipments (the repeated zone×slab total, which would
// flatten the blend to a plain mean). null if all masked (SL); plain mean if no shipment data.
const rcWavg = (arr, sel) => {
  const w = arr.filter((l) => sel(l) != null)
  if (!w.length) return null
  const tot = w.reduce((s, l) => s + laneShipments(l), 0)
  return tot > 0
    ? w.reduce((s, l) => s + sel(l) * laneShipments(l), 0) / tot
    : w.reduce((s, l) => s + sel(l), 0) / w.length
}

function rateCardMatrix(lanes = []) {
  const services = [...new Set(lanes.map((l) => l.serviceType))]
  const zones = [
    ...RC_ZONES.filter((z) => lanes.some((l) => l.zone === z)),
    ...[...new Set(lanes.map((l) => l.zone))].filter((z) => !RC_ZONES.includes(z)),
  ]
  const rows = []
  services.forEach((svc) => {
    const sl = lanes.filter((l) => l.serviceType === svc)
    // Volumes/splits use each lane's ACTUAL shipments (laneShipments), summed — so a
    // payment-mode slice (Prepaid/COD/RTO) reports that mode's own counts, not the blended total.
    const serviceShip = sl.reduce((s, l) => s + laneShipments(l), 0)
    // couriers ranked by allocation % (top 3 → P1/P2/P3; rank 4+ untagged)
    const alloc = {}
    sl.forEach((l) => { const c = l.courierParent || 'Blended'; alloc[c] = Math.max(alloc[c] || 0, l.allocationPct || 0) })
    const ranked = Object.keys(alloc).sort((a, b) => alloc[b] - alloc[a])
    const tag = {}
    ranked.forEach((c, i) => { if (alloc[c] > 0 && i < 3) tag[c] = ` [P${i + 1}]` })
    const slabs = [...new Set(sl.map((l) => l.weightSlab))].sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0))
    ranked.forEach((courier) => {
      const cLanesAll = sl.filter((l) => (l.courierParent || 'Blended') === courier)
      const courierShip = cLanesAll.reduce((s, l) => s + laneShipments(l), 0)
      slabs.forEach((slab) => {
        const cl = cLanesAll.filter((l) => l.weightSlab === slab)
        if (!cl.length) return
        const slabShip = cl.reduce((s, l) => s + laneShipments(l), 0)
        const cells = {}
        zones.forEach((z) => {
          const zl = cl.filter((l) => l.zone === z)
          cells[z] = zl.length ? {
            shipments: Math.round(zl.reduce((s, l) => s + laneShipments(l), 0)),
            marginPct: rc1(rcWavg(zl, (l) => l.marginPct)),
            marginAbs: rc2(rcWavg(zl, (l) => l.marginAbs)),
            targetRate: rc2(rcWavg(zl, (l) => l.revenueRate)),
            supplyRate: rc2(rcWavg(zl, (l) => l.costRate)),
          } : null
        })
        rows.push({
          service: svc,
          courier: `${courier}${tag[courier] || ''}`,
          splitPct: rc1(alloc[courier]),
          volume: Math.round(courierShip),
          wtFlagPct: courierShip ? rc1((slabShip / courierShip) * 100) : null, // slab's share of the courier
          overallPct: serviceShip ? rc1((slabShip / serviceShip) * 100) : null, // slab's share of the service
          weightSlab: slab,
          cells,
        })
      })
    })
  })
  return { zones, rows }
}

/** Preview rows (flat) for the Rate Card Margin Working pivot — the read-only facts,
 *  without the Excel-only "Final Rate"/"Margin % (Final)" what-if columns. */
export function rateCardMarginPreviewRows(lanes = []) {
  const { zones, rows } = rateCardMatrix(lanes)
  const dash = (v) => (v == null ? '—' : v)
  return rows.map((r) => {
    const o = {
      Service: svcLabel(r.service), Courier: r.courier, 'Split %': dash(r.splitPct),
      Volume: r.volume, 'Weight slab': r.weightSlab, 'Wt-flag %': dash(r.wtFlagPct), 'Overall %': dash(r.overallPct),
    }
    zones.forEach((z) => {
      const c = r.cells[z] || {}
      o[`${z} · Margin %`] = dash(c.marginPct)
      o[`${z} · Margin ₹`] = dash(c.marginAbs)
      o[`${z} · Target ₹`] = dash(c.targetRate)
      o[`${z} · Supply ₹`] = dash(c.supplyRate)
    })
    return o
  })
}

// v2 "Margin Working" — product's Required Format, styled via ExcelJS: three payment-mode
// tabs (RTO / Prepaid / COD), each a Service × Courier × Weight-Slab pivot in Measure→Zone
// layout — 7 identity columns + 7 measure groups, each broken out across the 5 fixed zones.
// Section-colored headers, number formats, section dividers, alternating shading, frozen
// identity columns + header, 80% zoom, hidden gridlines, tab colors, and negative-margin
// conditional formatting. Data/formulas mirror the pricing lanes exactly.
const RCV2_META = [
  { label: 'Service Type', w: 17, align: 'left', fmt: null },
  { label: 'Courier', w: 20, align: 'left', fmt: null },
  { label: 'Courier Split %', w: 9, align: 'center', fmt: '0.0"%"' },
  { label: 'Volume [ Courier Split ]', w: 10, align: 'center', fmt: '#,##0' },
  { label: 'Weight Slab', w: 10, align: 'left', fmt: null },
  { label: 'Split % (Courier x Weight_Slab)', w: 11, align: 'center', fmt: '0.0"%"' },
  { label: 'Split % (Overall)', w: 9, align: 'center', fmt: '0.0"%"' },
]
const RCV2_GROUPS = [
  { key: 'ship', label: 'Zone Wise Shipment Split', band: 'gray', fmt: '#,##0' },
  { key: 'mfinal', label: 'Margin % [ As Per Final Rate ]', band: 'green', fmt: '0.0"%"' },
  { key: 'mtgt', label: 'Margin % [ As Per Target Rate Card vs Supply Rate Card ]', band: 'green', fmt: '0.0"%"' },
  { key: 'mabs', label: 'Margin (Rs) [ As Per Target Rate Card vs Supply Rate Card ]', band: 'green', fmt: '#,##0.0' },
  { key: 'target', label: 'Target Rate (₹)', band: 'blue', fmt: '#,##0.0' },
  { key: 'supply', label: 'Supply Rate (₹)', band: 'blue', fmt: '#,##0.0' },
  { key: 'final', label: "Prozo's Final Rate (₹)", band: 'blue', fmt: '#,##0.0' },
]
const RC_FILL = { navy: 'FF1F3864', gray: 'FF595959', green: 'FF548235', blue: 'FF2F5597' }
const RC_TINT = { gray: 'FFF2F2F2', green: 'FFE2EFDA', blue: 'FFDDEBF7' }
const colLetter = (n) => { let s = ''; let x = n; while (x > 0) { const m = (x - 1) % 26; s = String.fromCharCode(65 + m) + s; x = Math.floor((x - 1) / 26) } return s }

/** Build one fully-styled payment-mode worksheet (ExcelJS). Zones are the fixed 5 so the layout
 *  matches product's template. "Prozo's Final Rate" defaults to Target and is editable; "Margin %
 *  [As Per Final Rate]" is a live formula = (Final − Supply)/Final × 100 (the Formula & Logic tab's
 *  written "− Target" is a typo; the sample data computes it vs Supply). Masked cost → blank. */
function buildMarginSheet(wb, name, sliceLanes, tabArgb) {
  const nMeta = RCV2_META.length
  const nZ = RC_ZONES.length
  const totalCols = nMeta + RCV2_GROUPS.length * nZ
  const ws = wb.addWorksheet(name, {
    views: [{ state: 'frozen', xSplit: nMeta, ySplit: 2, topLeftCell: `${colLetter(nMeta + 1)}3`, zoomScale: 80, showGridLines: false }],
    properties: { tabColor: { argb: tabArgb } },
  })
  const solid = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } })
  const thin = { style: 'thin', color: { argb: 'FFD9D9D9' } }
  const medium = { style: 'medium', color: { argb: 'FF808080' } }
  const whiteBold = (size) => ({ name: 'Arial', size, bold: true, color: { argb: 'FFFFFFFF' } })

  // ── Header: row 1 group labels, row 2 zone labels ──
  RCV2_META.forEach((m, i) => {
    const col = i + 1
    ws.mergeCells(1, col, 2, col)
    const c = ws.getCell(1, col)
    c.value = m.label
    c.fill = solid(RC_FILL.navy)
    c.font = whiteBold(10)
    c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    c.border = { bottom: medium }
  })
  RCV2_GROUPS.forEach((g, gi) => {
    const start = nMeta + gi * nZ + 1
    ws.mergeCells(1, start, 1, start + nZ - 1)
    const gc = ws.getCell(1, start)
    gc.value = g.label
    gc.fill = solid(RC_FILL[g.band])
    gc.font = whiteBold(10)
    gc.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    RC_ZONES.forEach((z, zi) => {
      const zc = ws.getCell(2, start + zi)
      zc.value = z
      zc.fill = solid(RC_TINT[g.band])
      zc.font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FF1F1F1F' } }
      zc.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
      zc.border = { bottom: medium }
    })
  })
  ws.getRow(1).height = 30
  ws.getRow(2).height = 30

  // ── Data ──
  const { rows } = rateCardMatrix(sliceLanes)
  if (!rows.length) {
    const note = ws.getCell(3, 1)
    note.value = `No ${name} lanes on this version.`
    note.font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF808080' } }
  }
  const sectionStarts = new Set(RCV2_GROUPS.map((_, gi) => nMeta + gi * nZ + 1)) // H, M, R, W, AB, AG, AL
  const groupBaseCol = (key) => nMeta + RCV2_GROUPS.findIndex((g) => g.key === key) * nZ + 1
  const supplyBase = groupBaseCol('supply'), finalBase = groupBaseCol('final')

  rows.forEach((r, ri) => {
    const excelRow = ri + 3
    const shade = ri % 2 === 1
    ws.getRow(excelRow).height = 18
    const metaVals = [svcLabel(r.service), r.courier, r.splitPct, r.volume, r.weightSlab, r.wtFlagPct, r.overallPct]
    RCV2_META.forEach((m, i) => {
      const cell = ws.getCell(excelRow, i + 1)
      if (metaVals[i] != null) cell.value = metaVals[i]
      if (m.fmt) cell.numFmt = m.fmt
    })
    RCV2_GROUPS.forEach((g, gi) => RC_ZONES.forEach((z, zi) => {
      const cell = ws.getCell(excelRow, nMeta + gi * nZ + zi + 1)
      const c = r.cells[z]
      if (c) {
        if (g.key === 'ship') cell.value = c.shipments
        else if (g.key === 'mtgt') cell.value = c.marginPct
        else if (g.key === 'mabs') cell.value = c.marginAbs
        else if (g.key === 'target') cell.value = c.targetRate
        else if (g.key === 'supply') cell.value = c.supplyRate
        else if (g.key === 'final') cell.value = c.targetRate // default = Target
        else if (g.key === 'mfinal' && c.supplyRate != null && c.targetRate != null) {
          const f = colLetter(finalBase + zi), s = colLetter(supplyBase + zi)
          cell.value = { formula: `IFERROR((${f}${excelRow}-${s}${excelRow})/${f}${excelRow}*100,"")`, result: c.targetRate ? rc1(((c.targetRate - c.supplyRate) / c.targetRate) * 100) : 0 }
        }
      }
      cell.numFmt = g.fmt
    }))
    for (let col = 1; col <= totalCols; col++) {
      const cell = ws.getCell(excelRow, col)
      const isText = col <= nMeta && RCV2_META[col - 1].align === 'left'
      cell.font = { name: 'Arial', size: 10 }
      cell.alignment = { horizontal: isText ? 'left' : 'center', vertical: 'middle' }
      cell.border = { top: thin, bottom: thin, right: thin, left: sectionStarts.has(col) ? { style: 'medium', color: { argb: 'FFBFBFBF' } } : thin }
      if (shade) cell.fill = solid('FFF7F9FC')
    }
  })

  // ── Column widths ──
  RCV2_META.forEach((m, i) => { ws.getColumn(i + 1).width = m.w })
  for (let col = nMeta + 1; col <= totalCols; col++) ws.getColumn(col).width = 9.5

  // ── Conditional formatting: negative margins (M:AA) bold red ──
  if (rows.length) {
    const start = colLetter(groupBaseCol('mfinal'))       // M
    const end = colLetter(groupBaseCol('mabs') + nZ - 1)  // AA
    ws.addConditionalFormatting({
      ref: `${start}3:${end}${rows.length + 2}`,
      rules: [{ type: 'cellIs', operator: 'lessThan', priority: 1, formulae: ['0'], style: { font: { bold: true, color: { argb: 'FFC00000' } } } }],
    })
  }
  return ws
}

/** Build + download the "Margin Working" .xlsx (product's Required Format) — three payment-mode
 *  tabs: RTO (computedRtoLanes), Prepaid & COD (computedLanes split by paymentMode). Fully styled
 *  via ExcelJS. Reviewer-only (cost/margin masked → blank for SL). Nothing is sent to the backend.
 *  exceljs is imported on demand so it stays out of the main bundle. */
export async function downloadRateCardMarginWorking(filename, lanes = [], rtoLanes = []) {
  const mod = await import('exceljs')
  const ExcelJS = mod.default || mod
  const wb = new ExcelJS.Workbook()
  const pm = (l) => String(l.paymentMode || '').toUpperCase()
  const slices = [
    { name: 'RTO', lanes: rtoLanes || [], tab: 'FF843C0C' },
    { name: 'Prepaid', lanes: lanes.filter((l) => pm(l).startsWith('PRE')), tab: 'FF2F5597' },
    { name: 'COD', lanes: lanes.filter((l) => pm(l).includes('COD')), tab: 'FFC55A11' },
  ]
  slices.forEach((s) => buildMarginSheet(wb, s.name, s.lanes, s.tab))
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
